/* eslint-disable react-refresh/only-export-components -- shadcn idiom: variants/helpers ship beside their component; HMR falls back to reload here. */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useControlRegister } from './control-register';
import { cn } from './utils';

/**
 * Button (02-design-system.md §4/§5): 44px default touch target, press
 * feedback via active:scale-95, shared flat styling across POS and Office.
 * `pay` is for money actions ONLY (Cash, Complete, paid states).
 */
const buttonVariants = cva(
  // font-mid (550), not bold. A button is already a filled, bordered, elevated
  // rectangle the size of a thumb; 700 on top of that was the fourth signal
  // saying the same thing, and with ~40 buttons on some POS screens it set the
  // page's whole texture. `xl` — the one-per-screen money CTA — keeps 650.
  //
  // Timing is now named (--duration-base / --ease-standard, tokens.css phase
  // 0.7) rather than the hardcoded `duration-150 ease-out` this shipped with.
  // 160ms instead of 150 and a slightly firmer decelerating curve: below the
  // just-noticeable difference on a colour fade, and it means the office's
  // sticky bars and disclosures can move at the same speed as a button
  // without anyone having to remember the number.
  /* SOFTER CORNERS ON A PHONE (owner, 2026-08-24: "button thora rounded karo
   mobile par"). Every size below sets `rounded-ctl` (4px), which is tuned for
   a dense mouse-driven toolbar where a button sits among inputs and selects at
   the same radius. A phone shows fewer, larger controls and they read as
   objects rather than as a row of fields, so they take the next step up the
   same scale — `rounded-card`, 8px. It is a media variant, so it is emitted
   after the unprefixed `rounded-ctl` and wins below md without `!`. Anything
   already pill-shaped (`rounded-full` on a caller) still wins, being later
   still in the merged class string. */
  'max-md:rounded-card inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-mid shadow-none transition-[background-color,border-color,color,box-shadow,transform] duration-base ease-standard active:scale-95 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 data-[loading=true]:[&>svg]:hidden',
  {
    variants: {
      variant: {
        /*
          BRAND TONE, WHITE LABEL (owner, 2026-08-24). This variant carried the
          ink that theme/contrast.ts computes, because white on a bright brand
          tone does not reach 4.5:1 — on the stock green it is 2.62:1. The owner
          asked for white and for the fill to stay exactly --primary, so that is
          what it is; the trade is written down where the pair is derived
          (applyTheme.ts) rather than repeated here.

          It goes through --primary-strong rather than --primary directly so
          there is ONE place to change if the decision is ever revisited — this
          variant, the lit chip and the register's cart bar all read that pair.
        */
        default:
          'bg-primary-strong text-primary-strong-foreground hover:bg-primary-strong-hover active:bg-primary-strong-hover',
        pay: 'bg-pay text-pay-foreground hover:bg-pay-hover',
        destructive: 'bg-danger-solid text-danger-foreground hover:opacity-90',
        'destructive-soft': 'bg-danger-tint text-danger-tint-foreground hover:bg-danger-tint-hover',
        outline:
          'border border-border bg-card text-text hover:border-primary hover:text-primary-text',
        secondary: 'bg-well text-text hover:bg-well-hover',
        ghost: 'text-muted hover:bg-well hover:text-text',
        ink: 'bg-ink text-ink-foreground hover:bg-ink-2',
        link: 'text-primary-text underline-offset-4 hover:underline',
      },
      size: {
        /*
          `sm` and `icon-sm` are 36px, which is BELOW the 44px target §4
          requires — and they are not a rare compact case: a mobile audit found
          them carrying Accept/Ready/Delivered on every queue card, plus
          Refresh, Z-report, Add printer, Switch and Log out. Twelve failing
          controls on one tablet screen.

          `hit-area` is the fix the design system already has (tokens.css): a
          centred, transparent 44px ::after that costs no layout, so a dense
          desktop toolbar keeps its 36px rhythm while a finger gets the full
          target. Row gaps are ≥6px and the box only extends 4px per side, so
          it cannot steal a neighbour's tap.

          Visual size is a separate question from tap size, and `pointer-coarse`
          answers it where it matters — see the queue card's primary action.

          IT NOW ANSWERS IT HERE TOO (MOBILE-RESPONSIVE-PLAN phase 0). The
          ::after was always a compromise: it wins the tap but it leaves the
          control LOOKING like a 36px desk control on a surface being driven by
          a thumb, and the office audit at 390px counted the same twelve
          controls again — "More actions" and the account menu among them —
          because a bounding box is what an audit can see. Where the pointer is
          coarse both steps grow to the real 44, and `hit-area` becomes a no-op
          rather than a lie: its ::after is `max(100%, 44px)`, so at 44 it is
          exactly the box. The 36px rhythm survives untouched on every mouse.
        */
        /*
          `xs` is the office's dense step (OFFICE-UI-REDESIGN-PLAN §3): 28px,
          the height a Polaris or Intercom toolbar sits at, for a filter row or
          a row action that must not eat 44px of a 52px table row.

          It deliberately does NOT take `hit-area`, and that is a departure
          from `sm` above rather than an oversight. The argument for `hit-area`
          at 36px was that its 44px ::after only extends 4px per side, so it
          cannot steal a neighbour's tap. At 28px it would extend 8px per side,
          which is wider than the gap in every dense toolbar this size exists
          for — two adjacent filters would overlap and the later one in DOM
          order would swallow taps meant for the first. 28px still clears WCAG
          2.5.8 (24×24, AA), and the surface is seated and mouse-driven.

          Where the pointer is NOT a mouse, the control grows instead of
          growing an invisible box: `pointer-coarse` takes it to the full 44px
          --control-lg, the same escape hatch select.tsx:24 already uses.
        */
        xs: 'h-control-xs rounded-ctl px-2.5 text-label pointer-coarse:h-control-lg pointer-coarse:px-3.5',
        /* Square counterpart of `xs`, for a table row's ⋯ / edit / delete
           glyphs. `aspect-square` rather than a width utility because the
           density register maps into Tailwind's `--height` namespace only, so
           `h-control-xs` has no `w-` twin to pair with. */
        'icon-xs': 'aspect-square h-control-xs rounded-ctl pointer-coarse:h-control-lg',
        sm: 'hit-area h-9 rounded-ctl px-3.5 text-label pointer-coarse:h-control-lg',
        /* 32px, the same --control-sm the office Input and SelectTrigger
           `compact` step uses, for dense toolbars that ask for it by name. The
           console's own default is `sm` (36px) — control-register.tsx. */
        compact: 'h-control-sm rounded-ctl px-3 text-label pointer-coarse:h-control-lg',
        'icon-compact': 'aspect-square h-control-sm rounded-ctl pointer-coarse:h-control-lg',
        default: 'h-11 rounded-ctl px-4 text-sm',
        lg: 'h-[52px] rounded-card px-5 text-[15px]',
        xl: 'h-[60px] rounded-card px-6 text-[17px] font-strong tracking-[-0.015em]',
        icon: 'size-11 rounded-ctl',
        /* `size-11` and not `h-control-lg`: this one is square, and the density
           register maps into Tailwind's `--height` namespace only (tokens.css
           @theme inline), so there is no `size-control-lg` to reach for. 44px
           either way — it is the number `icon` above is already spelled with. */
        'icon-sm': 'hit-area size-9 rounded-ctl pointer-coarse:size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ComponentPropsWithRef<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * In-flight async action. Swaps the button's own icon for a spinner, marks
   * the element `aria-busy` and blocks further presses.
   *
   * There was no busy state before, so the office invented one per page: the
   * products list dims the ENTIRE table row while a status toggle is in flight
   * (OfficeProductsPage.tsx:345), which tells you something is happening
   * somewhere in the row but not which of the three controls you pressed, and
   * says nothing at all to a screen reader.
   *
   * The label stays. Replacing "Save changes" with a bare spinner reflows the
   * button — and in a toolbar, everything after it — at the exact moment the
   * user is watching for a result; a jumping control reads as a bug, not as
   * progress. Only the glyph is swapped, so a labelled button holds its width
   * to the pixel and an icon-only one holds its square.
   *
   * Ignored under `asChild`: Slot renders the caller's single child, so there
   * is no slot to put a spinner in. `aria-busy` and `disabled` still pass
   * through to that child.
   */
  loading?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  type,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  // Office uses the 36px `sm` step; POS retains 44px touch targets.
  // Both use the same flat variant styles.
  const desk = useControlRegister() === 'desk';
  const resolvedSize = desk ? deskButtonSize(size) : size;
  return (
    <Comp
      data-slot="button"
      // Read by surfaces that restyle buttons they did not create — the phone
      // ⋯ sheet keeps a destructive action red after flattening it into a row.
      data-variant={variant ?? undefined}
      // Read by the base recipe's `data-[loading=true]:[&>svg]:hidden`, which
      // is what makes the spinner a REPLACEMENT for the caller's leading icon
      // rather than a second glyph beside it. The direct-child selector is
      // load-bearing: the spinner below sits inside a display:contents span,
      // so it lays out as a flex item of the button while staying out of
      // reach of `& > svg`.
      data-loading={loading ? 'true' : undefined}
      aria-busy={loading || undefined}
      disabled={asChild ? disabled : disabled || loading}
      className={cn(buttonVariants({ variant, size: resolvedSize }), className)}
      {...(asChild ? {} : { type: type ?? 'button' })}
      {...props}
    >
      {/*
        Under `asChild` this MUST be the caller's child and nothing else — not
        even a falsy sibling.

        Guarding the spinner with `loading && !asChild` is not enough: two JSX
        expressions side by side compile to a two-element children ARRAY, and
        `null` still occupies a slot in it. Radix's Slot then refuses with
        "Expected a single React element child or `Slottable`" — which is what
        every `asChild` button in the app did the moment `loading` was added,
        starting with the topbar's "Open register" (OfficeShell.tsx:718).
      */}
      {asChild ? (
        children
      ) : (
        <>
          {loading ? (
            <span className="contents" data-slot="button-spinner">
              {/* aria-hidden: aria-busy on the button is the announcement, and a
                  second one from a decorative glyph would be noise. */}
              <CircleNotchIcon size={16} weight="bold" className="animate-spin" aria-hidden />
            </span>
          ) : null}
          {children}
        </>
      )}
    </Comp>
  );
}

/** The desk register's step for a requested button size (control-register.tsx). */
function deskButtonSize(size: ButtonProps['size']): ButtonProps['size'] {
  if (size === undefined || size === null || size === 'default') return 'sm';
  if (size === 'icon') return 'icon-sm';
  return size;
}

export { Button, buttonVariants };
